import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { Download, Share2, Edit, ExternalLink, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { BUCKET_URL, INTAPP_URL, WEB_APP_URL } from '../../../../config/config';

interface DigitalCardViewerProps {
  cardData: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    whatsapp?: string;
    designation?: string;
    companyName?: string;
    companyAddress?: string;
    briefDescription?: string;
    website?: string;
    facebook?: string;
    instagram?: string;
    linkedIn?: string;
    googleCalendar?: string;
    cardUrl?: string;
    uniqueLinkId?: string;
    viewedCount?: number;
  };
  onEdit: () => void;
  onRefreshPreview?: () => void;
}

const DigitalCardViewer: React.FC<DigitalCardViewerProps> = ({ cardData, onEdit, onRefreshPreview }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Get auth token and strip 'Bearer ' if present
  const getCleanToken = () => {
    const token = localStorage.getItem('auth_token') || '';
    return token.replace(/^Bearer\s+/i, '');
  };

  // Reload iframe to get latest card
  const reloadPreview = () => {
    if (iframeRef.current) {
      // Force iframe reload by appending timestamp
      const baseUrl = webviewUrl.split('?')[0];
      const params = new URLSearchParams(webviewUrl.split('?')[1] || '');
      params.set('t', Date.now().toString());
      iframeRef.current.src = `${baseUrl}?${params.toString()}`;
      toast.success('Preview refreshed');
    }
    if (onRefreshPreview) {
      onRefreshPreview();
    }
  };

  // Construct webview URL for live preview (uses INTAPP_URL from config)
  const webviewUrl = `${INTAPP_URL}bussinessCard?token=${getCleanToken()}`;

  // Construct PDF URL for download
  const pdfUrl = cardData.cardUrl ? `${BUCKET_URL}${cardData.cardUrl}` : '';

  // Construct share URL (use production WEB_APP_URL instead of localhost)
  const shareUrl = cardData.uniqueLinkId
    ? `${WEB_APP_URL}/share/Card/${cardData.uniqueLinkId}`
    : '';

  const handleDownload = async () => {
    if (!pdfUrl) {
      toast.error('PDF not available');
      return;
    }

    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${cardData.name}-BusinessCard.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download card');
    }
  };

  const handleShare = () => {
    if (!shareUrl) {
      toast.error('Share link not available');
      return;
    }

    const message = `Hi, here's my digital business card:\n${shareUrl}`;

    if (navigator.share) {
      navigator.share({
        title: `${cardData.name} - Business Card`,
        text: message,
        url: shareUrl
      }).catch(() => {
        // Fallback to copy
        copyShareLink();
      });
    } else {
      copyShareLink();
    }
  };

  const copyShareLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const openInNewTab = () => {
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    } else {
      toast.error('Share link not available');
    }
  };

  return (
    <ViewerContainer>
      <CardPreview>
        {webviewUrl ? (
          <PDFFrame>
            <iframe
              ref={iframeRef}
              src={webviewUrl}
              title="Digital Business Card - Live Preview"
              onLoad={() => setImageLoaded(true)}
            />
            {!imageLoaded && <LoadingOverlay>Loading card...</LoadingOverlay>}
          </PDFFrame>
        ) : (
          <EmptyState>
            <EmptyIcon>📇</EmptyIcon>
            <EmptyText>Card preview not available</EmptyText>
          </EmptyState>
        )}
      </CardPreview>

      <CardInfo>
        <InfoHeader>
          <InfoTitle>{cardData.name}</InfoTitle>
          {cardData.designation && <InfoSubtitle>{cardData.designation}</InfoSubtitle>}
          {cardData.companyName && <InfoCompany>{cardData.companyName}</InfoCompany>}
        </InfoHeader>


        <ActionButtons>
          <ActionButton primary onClick={onEdit}>
            <Edit size={18} />
            Edit Card
          </ActionButton>

          <ActionButton onClick={reloadPreview}>
            <RefreshCw size={18} />
            Refresh Preview
          </ActionButton>

          {pdfUrl && (
            <ActionButton onClick={handleDownload}>
              <Download size={18} />
              Download PDF
            </ActionButton>
          )}

          {shareUrl && (
            <>
              <ActionButton onClick={handleShare}>
                <Share2 size={18} />
                Share
              </ActionButton>

              <ActionButton onClick={openInNewTab}>
                <ExternalLink size={18} />
                Open Link
              </ActionButton>

              <ActionButton onClick={copyShareLink}>
                <Copy size={18} />
                Copy Link
              </ActionButton>
            </>
          )}
        </ActionButtons>

        {shareUrl && (
          <ShareSection>
            <ShareLabel>Share Link</ShareLabel>
            <ShareLinkBox>
              <ShareLinkText>{shareUrl}</ShareLinkText>
              <CopyIconButton onClick={copyShareLink}>
                <Copy size={14} />
              </CopyIconButton>
            </ShareLinkBox>
          </ShareSection>
        )}
      </CardInfo>
    </ViewerContainer>
  );
};

export default DigitalCardViewer;

// Styled Components
const ViewerContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
  padding: 24px;
  background: #f8fafc;
  border-radius: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const CardPreview = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;
`;

const PDFFrame = styled.div`
  width: 100%;
  height: 600px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Gilroy', sans-serif;
  color: #64748b;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #94a3b8;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-family: 'Gilroy', sans-serif;
  font-size: 16px;
  margin: 0;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoHeader = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const InfoTitle = styled.h2`
  font-family: 'Gilroy', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const InfoSubtitle = styled.p`
  font-family: 'Gilroy', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #3b82f6;
  margin: 0 0 4px 0;
`;

const InfoCompany = styled.p`
  font-family: 'Gilroy', sans-serif;
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const StatItem = styled.div`
  flex: 1;
  text-align: center;
  padding: 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 8px;
`;

const StatValue = styled.div`
  font-family: 'Gilroy', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-family: 'Gilroy', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-family: 'Gilroy', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.primary ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    grid-column: 1 / -1;

    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
      transform: translateY(-1px);
    }
  ` : `
    background: white;
    color: #64748b;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    &:hover {
      background: #f8fafc;
      color: #475569;
      border-color: #cbd5e1;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
  `}

  &:active {
    transform: translateY(0);
  }

  svg {
    flex-shrink: 0;
  }
`;

const ShareSection = styled.div`
  background: white;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const ShareLabel = styled.div`
  font-family: 'Gilroy', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const ShareLinkBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
`;

const ShareLinkText = styled.div`
  flex: 1;
  font-family: 'Gilroy', sans-serif;
  font-size: 13px;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyIconButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    color: #3b82f6;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
